/**
 * App — Router and top-level context providers
 */
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useAuth } from './hooks/useAuth';
import { useCart } from './hooks/useCart';
import React, { createContext, useContext, useState, useEffect } from 'react';

// Pages
import LandingPage        from './pages/LandingPage';
import ShopPage           from './pages/ShopPage';
import ProductRouter      from './pages/ProductRouter';
import CartPage           from './pages/CartPage';
import CartRecoverPage    from './pages/CartRecoverPage';
import CheckoutPage       from './pages/CheckoutPage';
import OrderSuccessPage   from './pages/OrderSuccessPage';
import ThankYouPage       from './pages/ThankYouPage';
import LoginPage          from './pages/LoginPage';
import RegisterPage       from './pages/RegisterPage';
import AccountPage        from './pages/AccountPage';
import DashboardPage      from './pages/DashboardPage';
import AdminPage          from './pages/AdminPage';
import NotFoundPage       from './pages/NotFoundPage';
import VerifyEmailPage    from './pages/VerifyEmailPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import AboutPage          from './pages/AboutPage';
import SupportPage        from './pages/SupportPage';
import TermsPage          from './pages/TermsPage';
import PrivacyPage        from './pages/PrivacyPage';
import CompliancePage     from './pages/CompliancePage';
import PressPage          from './pages/PressPage';
import PressPostPage      from './pages/PressPostPage';
import AmbassadorsPage    from './pages/AmbassadorsPage';
import UnsubscribePage    from './pages/UnsubscribePage';
import MaintenanceGate      from './components/MaintenanceGate';
import AnalyticsTracker     from './components/AnalyticsTracker';
import ScrollToTop          from './components/ScrollToTop';
import ErrorBoundary        from './components/ErrorBoundary';

// ─── Auth + Cart + DarkMode contexts ────────────────────────────────────────
export const AuthContext     = createContext(null);
export const CartContext     = createContext(null);
export const DarkModeContext = createContext(null);
export const useAuthCtx     = () => useContext(AuthContext);
export const useCartCtx     = () => useContext(CartContext);
export const useDarkMode    = () => useContext(DarkModeContext);

/** Protected route wrapper */
const ProtectedRoute = ({ children, roles }) => {
  const { user } = useAuthCtx();
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
};

/** Admins land on the god-tier Users console instead of the referral dashboard */
const AffiliateOrAdminRedirect = () => {
  const { user } = useAuthCtx();
  if (user?.role === 'admin') return <Navigate to="/admin/users" replace />;
  return <DashboardPage />;
};

const DashboardOrAdminRedirect = () => {
  const { user } = useAuthCtx();
  if (user?.role === 'admin') return <Navigate to="/admin/users" replace />;
  return <DashboardPage />;
};

export default function App() {
  const auth = useAuth();
  const cart = useCart();

  const [darkMode, setDarkMode] = useState(() => {
    try { return localStorage.getItem('ab8beauty-dark') === 'true'; } catch { return false; }
  });

  useEffect(() => {
    document.documentElement.classList.toggle('dark', darkMode);
    try { localStorage.setItem('ab8beauty-dark', String(darkMode)); } catch {}
  }, [darkMode]);

  const toggleDarkMode = () => setDarkMode(prev => !prev);

  return (
    <DarkModeContext.Provider value={{ darkMode, toggleDarkMode }}>
    <AuthContext.Provider value={auth}>
      <CartContext.Provider value={cart}>
        <BrowserRouter>
          {/* Snap back to the top whenever the route changes */}
          <ScrollToTop />
          {/* First-party pageview / live-user tracking (storefront only) */}
          <AnalyticsTracker />
          {/* Branded "we'll be back shortly" page when the admin pauses the site */}
          <MaintenanceGate />
          <ErrorBoundary>
          <Routes>
            {/* Public — storefront is open to anonymous shoppers */}
            <Route path="/"                element={<LandingPage />} />
            <Route path="/shop"            element={<ShopPage />} />
            <Route path="/shop/:slug"      element={<ProductRouter />} />
            <Route path="/login"           element={<LoginPage />} />
            <Route path="/register"        element={<RegisterPage />} />
            <Route path="/verify-email"    element={<VerifyEmailPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/about"           element={<AboutPage />} />
            <Route path="/support"         element={<SupportPage />} />
            <Route path="/terms"           element={<TermsPage />} />
            <Route path="/privacy"         element={<PrivacyPage />} />
            <Route path="/compliance"      element={<CompliancePage />} />
            <Route path="/press"           element={<PressPage />} />
            <Route path="/press/:slug"     element={<PressPostPage />} />
            <Route path="/ambassadors"     element={<AmbassadorsPage />} />
            <Route path="/unsubscribe"     element={<UnsubscribePage />} />

            {/* Customer */}
            <Route path="/cart"     element={<CartPage />} />
            <Route path="/cart/recover/:id" element={<CartRecoverPage />} />
            <Route path="/checkout" element={
              <ProtectedRoute><CheckoutPage /></ProtectedRoute>
            } />
            <Route path="/order-success/:id" element={
              <ProtectedRoute><OrderSuccessPage /></ProtectedRoute>
            } />
            <Route path="/thank-you/:id" element={
              <ProtectedRoute><ThankYouPage /></ProtectedRoute>
            } />
            <Route path="/account" element={
              <ProtectedRoute><AccountPage /></ProtectedRoute>
            } />
            <Route path="/dashboard" element={
              <ProtectedRoute><DashboardOrAdminRedirect /></ProtectedRoute>
            } />

            {/* Affiliate — admins get redirected to the god-tier Users console */}
            <Route path="/affiliate/*" element={
              <ProtectedRoute roles={['affiliate', 'admin']}>
                <AffiliateOrAdminRedirect />
              </ProtectedRoute>
            } />

            {/* Admin */}
            <Route path="/admin/*" element={
              <ProtectedRoute roles={['admin']}><AdminPage /></ProtectedRoute>
            } />

            <Route path="*" element={<NotFoundPage />} />
          </Routes>
          </ErrorBoundary>

          <ToastContainer position="bottom-right" autoClose={4000} />
        </BrowserRouter>
      </CartContext.Provider>
    </AuthContext.Provider>
    </DarkModeContext.Provider>
  );
}
